import type { Workbook, Rect } from "@fg/web-core";
import { cssToArgb } from "./geometry";

// The previous editor saved workbooks as JSON snapshots ({ Workbook: { sheets: [...] } }) and the
// built-in "start a spreadsheet" templates are written in the same shape. This turns that JSON into
// engine operations so both keep opening. Only what the snapshot format carries is read: values,
// formulas, number formats, fonts, fills, alignment, column widths, row heights and frozen panes.

interface JsonCell {
    index?: number;
    value?: string | number | boolean | null;
    formula?: string;
    format?: string;
    style?: Record<string, string | undefined>;
}
interface JsonRow {
    index?: number;
    height?: number;
    cells?: JsonCell[];
}
interface JsonSheet {
    name?: string;
    rows?: JsonRow[];
    columns?: Array<{ index?: number; width?: number } | null>;
    frozenRows?: number;
    frozenColumns?: number;
}

const MAX_STYLED_CELLS = 20_000;

const hex = (c: string | undefined): number | null => (c && /^#[0-9a-f]{6}$/i.test(c.trim()) ? cssToArgb(c.trim()) : null);

function points(size: string | undefined): number | null {
    if (!size) return null;
    const m = /^([\d.]+)\s*(pt|px)?$/.exec(size.trim());
    if (!m) return null;
    const n = Number(m[1]);
    return m[2] === "px" ? Math.round((n * 72) / 96) : n;
}

/** Whether `json` looks like a workbook snapshot. */
export function isWorkbookJson(json: unknown): boolean {
    const w = (json as { Workbook?: { sheets?: unknown } } | null)?.Workbook;
    return Array.isArray(w?.sheets);
}

/** Replaces the workbook's contents with the snapshot. */
export async function applyWorkbookJson(wb: Workbook, json: unknown): Promise<void> {
    const sheets = (json as { Workbook?: { sheets?: JsonSheet[] } }).Workbook?.sheets;
    if (!Array.isArray(sheets) || sheets.length === 0) throw new Error("This file has no worksheets.");
    await wb.newDocument();
    for (let s = 0; s < sheets.length; s++) {
        const sh = sheets[s]!;
        const name = sh.name?.trim() || `Sheet${s + 1}`;
        if (s === 0) await wb.renameSheet(0, name);
        else await wb.addSheet(name);

        // Values and formulas as one block.
        const grid: string[][] = [];
        const styled: Array<{ r: number; c: number; cell: JsonCell }> = [];
        let r = 0;
        for (const row of sh.rows ?? []) {
            if (row?.index !== undefined) r = row.index;
            let c = 0;
            for (const cell of row?.cells ?? []) {
                if (cell?.index !== undefined) c = cell.index;
                if (cell) {
                    const input = cell.formula ? (cell.formula.startsWith("=") ? cell.formula : `=${cell.formula}`) : cell.value === undefined || cell.value === null ? "" : String(cell.value);
                    if (input !== "") {
                        while (grid.length <= r) grid.push([]);
                        const line = grid[r]!;
                        while (line.length < c) line.push("");
                        line[c] = input;
                    }
                    if ((cell.style && Object.keys(cell.style).length) || cell.format) styled.push({ r, c, cell });
                }
                c++;
            }
            if (row?.height) await wb.setRowHeightPx(r, row.height, s);
            r++;
        }
        if (grid.some((l) => l.length)) await wb.setRangeInputs(0, 0, grid.map((l) => l.map((x) => x ?? "")), s);

        // Formatting, cell by cell (snapshots are small; a runaway one is capped).
        for (const { r: rr, c: cc, cell } of styled.slice(0, MAX_STYLED_CELLS)) {
            const rect: Rect = [rr, cc, rr, cc];
            const st = cell.style ?? {};
            if (st.fontWeight === "bold") await wb.setBold(rect, true, s);
            if (st.fontStyle === "italic") await wb.setItalic(rect, true, s);
            if (st.textDecoration?.includes("underline")) await wb.setUnderline(rect, true, s);
            const pt = points(st.fontSize);
            if (pt) await wb.setFontSize(rect, pt, s);
            const color = hex(st.color);
            if (color !== null) await wb.setFontColor(rect, color, s);
            const fill = hex(st.backgroundColor);
            if (fill !== null) await wb.setFill(rect, fill, s);
            if (st.textAlign === "left" || st.textAlign === "center" || st.textAlign === "right") await wb.setAlign(rect, st.textAlign, s);
            if (cell.format) await wb.setNumberFormat(rect, cell.format, s);
        }

        let c = 0;
        for (const col of sh.columns ?? []) {
            if (col?.index !== undefined) c = col.index;
            if (col?.width) await wb.setColWidthPx(c, col.width, s);
            c++;
        }
        if ((sh.frozenRows ?? 0) > 0 || (sh.frozenColumns ?? 0) > 0) await wb.setFreeze(sh.frozenRows ?? 0, sh.frozenColumns ?? 0, s);
    }
    await wb.setActiveSheet(0);
}
